import {
  FormattedOfficeHour,
  OfficeHour,
  OfficeHoursApiResponse,
} from '../types';
import { timeZoneMapping } from './timeZoneMapping';

/**
 * Office Hours Formatting Utilities
 *
 * IMPORTANT: Proper sorting is crucial for lobby closure hours display.
 * Sorting priority:
 * 1. end_of_day flag (last slot of the day comes last)
 * 2. open_time (within same end_of_day group, sort chronologically)
 *
 * For display purposes, office hours are shown in split segments so we can
 * explicitly call out lobby closures between blocks (e.g.
 * `8:00 AM - 12:00 PM, Lobby Closure 12:00 PM - 1:00 PM, 1:00 PM - 5:00 PM`).
 */
export const daysOfWeek = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const formatTime = (time: string | null): string => {
  if (!time) return '';

  // Handle 12-hour format with AM/PM (e.g., "2:30 PM", "12:00 AM")
  if (time.includes('AM') || time.includes('PM')) {
    return time; // Already in 12-hour format, return as is
  }

  // Convert 24-hour format to 12-hour AM/PM
  const [hour, minute] = time.split(':').map(Number);
  if (isNaN(hour) || isNaN(minute)) return '';

  let hour12 = hour;
  let meridiem = 'AM';

  if (hour === 0) {
    hour12 = 12;
    meridiem = 'AM';
  } else if (hour === 12) {
    hour12 = 12;
    meridiem = 'PM';
  } else if (hour > 12) {
    hour12 = hour - 12;
    meridiem = 'PM';
  }

  return `${hour12}:${minute
    .toString()
    .padStart(2, '0')} ${meridiem}`;
};

export const formatOfficeHours = (
  apiResponse: OfficeHoursApiResponse
): FormattedOfficeHour[] => {
  return daysOfWeek.map((day, index) => {
    const entries = apiResponse.office_hours
      .filter((item) => item.day_of_week === index + 1)
      .sort((a, b) => {
        // First sort by end_of_day (last slot should come last)
        // Then by open_time for consistent ordering within same end_of_day group
        const aFlag = a.end_of_day ? 1 : 0;
        const bFlag = b.end_of_day ? 1 : 0;
        if (aFlag !== bFlag) {
          return aFlag - bFlag;
        }
        // If both have same end_of_day status, sort by open_time using proper time comparison
        const aTime = a.open_time || '';
        const bTime = b.open_time || '';

        // Convert to minutes for proper time comparison instead of string comparison
        const aMinutes = aTime
          ? parseInt(aTime.split(':')[0]) * 60 +
            parseInt(aTime.split(':')[1])
          : 0;
        const bMinutes = bTime
          ? parseInt(bTime.split(':')[0]) * 60 +
            parseInt(bTime.split(':')[1])
          : 0;

        return aMinutes - bMinutes;
      });

    if (entries.length === 0 || entries.every((e) => e.closed)) {
      return { day, hours: 'Closed' };
    }

    // Filter out closed entries when building hour strings
    const openEntries = entries.filter((entry) => !entry.closed);

    if (openEntries.length === 0) {
      return { day, hours: 'Closed' };
    }

    const segments: string[] = [];

    openEntries.forEach((entry, index) => {
      const open = formatTime(entry.open_time || '');
      const close = formatTime(entry.close_time || '');

      if (open && close) {
        segments.push(`${open} - ${close}`);
      }

      const nextEntry = openEntries[index + 1];
      if (nextEntry && entry.close_time && nextEntry.open_time) {
        const closeMinutes =
          parseInt(entry.close_time.split(':')[0] ?? '0', 10) * 60 +
          parseInt(entry.close_time.split(':')[1] ?? '0', 10);
        const nextOpenMinutes =
          parseInt(nextEntry.open_time.split(':')[0] ?? '0', 10) * 60 +
          parseInt(nextEntry.open_time.split(':')[1] ?? '0', 10);

        if (closeMinutes < nextOpenMinutes) {
          segments.push(
            `Lobby Closure ${formatTime(entry.close_time)} - ${formatTime(
              nextEntry.open_time
            )}`
          );
        }
      }
    });

    return {
      day,
      hours: segments.join(', '),
    };
  });
};

export const getPracticeStatusWithLunch = (
  timezone: string,
  rawOfficeHours: OfficeHour[]
): {
  status: string;
  lunchEndsIn?: number;
  lunchStart?: string;
  lunchEnd?: string;
} => {
  const IanaTimeZone = timeZoneMapping[timezone] || 'UTC';
  const currentDate = new Date(
    new Date().toLocaleString('en-US', { timeZone: IanaTimeZone })
  );
  const todayIndex = currentDate.getDay(); // 0 = Sunday, so matches OfficeHour.day_of_week - 1

  const todayBlocks = rawOfficeHours
    .filter(
      (item) => item.day_of_week === todayIndex + 1 && !item.closed
    )
    .sort((a, b) => {
      // First sort by end_of_day (last slot should come last)
      // Then by open_time for consistent ordering within same end_of_day group
      const aFlag = a.end_of_day ? 1 : 0;
      const bFlag = b.end_of_day ? 1 : 0;
      if (aFlag !== bFlag) {
        return aFlag - bFlag;
      }
      // If both have same end_of_day status, sort by open_time using proper time comparison
      const aTime = a.open_time || '';
      const bTime = b.open_time || '';

      // Convert to minutes for proper time comparison instead of string comparison
      const aMinutes = aTime
        ? parseInt(aTime.split(':')[0]) * 60 +
          parseInt(aTime.split(':')[1])
        : 0;
      const bMinutes = bTime
        ? parseInt(bTime.split(':')[0]) * 60 +
          parseInt(bTime.split(':')[1])
        : 0;

      return aMinutes - bMinutes;
    });

  if (!todayBlocks.length) return { status: 'CLOSED' };

  for (const block of todayBlocks) {
    const start = parseTime(block.open_time!, currentDate);
    const end = parseTime(block.close_time!, currentDate);

    if (currentDate >= start && currentDate < end) {
      // Inside this block
      const minutesLeft = Math.floor(
        (end.getTime() - currentDate.getTime()) / 60000
      );
      if (minutesLeft <= 30) return { status: 'CLOSING SOON' };
      return { status: 'OPEN' };
    }
  }

  // Handle lunch gap
  if (todayBlocks.length === 2) {
    const endOfMorning = parseTime(
      todayBlocks[0].close_time!,
      currentDate
    );
    const startOfPostLunch = parseTime(
      todayBlocks[1].open_time!,
      currentDate
    );

    if (
      currentDate >= endOfMorning &&
      currentDate < startOfPostLunch
    ) {
      const minutesUntilLunchEnds = Math.floor(
        (startOfPostLunch.getTime() - currentDate.getTime()) / 60000
      );
      return {
        status: 'LUNCH',
        lunchEndsIn: minutesUntilLunchEnds,
        lunchStart: todayBlocks[0].close_time!,
        lunchEnd: todayBlocks[1].open_time!,
      };
    }
  }

  return { status: 'CLOSED' };
};

const parseTime = (time: string, reference: Date) => {
  const [hour, minute] = time.split(':').map(Number);
  const date = new Date(reference);
  date.setHours(hour, minute, 0, 0);
  return date;
};
